import { useEffect, useState } from "react";

import LoadingSpinner from "@/components/common/common/LoadingSpinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGetUserPopupList } from "@/service/user/popup";
import { useUserPopupStore } from "@/store/user/useUserPopupStore";

interface PopupListItem {
    idx: number;
    p_e_date: string;
    p_width_size: number;
    p_height_size: number;
    p_left_point: number;
    p_top_point: number;
    p_content: string;
    p_open: string[];
    p_one_day: string[];
    p_s_date: string; // 시작일
}

export default function UserPop() {
    const [type, setType] = useState<string>("");
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
    const { data: configData } = useGetUserPopupList(type, { enabled: !!type });
    const [popupList, setPopupList] = useState<PopupListItem[]>([]);
    const [isContentLoaded, setIsContentLoaded] = useState<{ [key: number]: boolean }>({});
    const { setClosedPopup, setOneDayClosedPopup, isPopupClosed } = useUserPopupStore();

    //화면사이즈 변경될때 width 체크---------
    useEffect(() => {
        const handleWindowResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleWindowResize);
        // 초기 화면 크기 설정
        setType(window.innerWidth >= 768 ? "P" : "M");

        return () => {
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    // 화면 크기가 변경될 때마다 type 업데이트
    useEffect(() => {
        setType(windowWidth >= 768 ? "P" : "M");
    }, [windowWidth]);

    // 팝업 목록 조회
    useEffect(() => {
        if (configData) {
            const data = configData.data.popup_list;
            const list = data.reverse();

            const getDateString = (date: Date) => date.toISOString().slice(0, 10);

            const currentDateStr = getDateString(new Date());

            const popList = list.filter((item: PopupListItem) => {
                if (item.p_open.includes("N")) return false;

                // 시작일 체크
                if (item.p_s_date) {
                    const startDate = new Date(item.p_s_date.replace(/\./g, "-"));
                    const startDateStr = getDateString(startDate);
                    if (startDateStr > currentDateStr) return false;
                }

                // 종료일 체크
                if (item.p_e_date) {
                    const endDate = new Date(item.p_e_date.replace(/\./g, "-"));
                    const endDateStr = getDateString(endDate);
                    if (endDateStr < currentDateStr) return false; // 오늘까지 노출
                }

                return true;
            });
            setPopupList(popList);
        }
    }, [configData]);

    // 컨텐츠 로딩 완료 체크
    useEffect(() => {
        popupList.forEach(popup => {
            if (popup.p_content && !isContentLoaded[popup.idx]) {
                // 먼저 컨텐츠를 렌더링
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = popup.p_content;
                const images = tempDiv.getElementsByTagName("img");

                if (images.length === 0) {
                    setIsContentLoaded(prev => ({ ...prev, [popup.idx]: true }));
                    return;
                }

                let loadedImages = 0;
                Array.from(images).forEach(img => {
                    if (img.complete) {
                        loadedImages++;
                        if (loadedImages === images.length) {
                            setIsContentLoaded(prev => ({ ...prev, [popup.idx]: true }));
                        }
                    } else {
                        img.onload = () => {
                            loadedImages++;
                            if (loadedImages === images.length) {
                                setIsContentLoaded(prev => ({ ...prev, [popup.idx]: true }));
                            }
                        };
                    }
                });
            }
        });
    }, [popupList, isContentLoaded]);

    // 닫은 팝업 필터링
    const filteredPopups = popupList.filter((item: PopupListItem) => !isPopupClosed(item.idx));

    return (
        <>
            {filteredPopups.map((popup, i) => (
                <div
                    key={`popup_${i}`}
                    className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full z-[500]"
                >
                    <div
                        className="absolute"
                        style={{
                            top: popup.p_top_point,
                            left: popup.p_left_point,
                        }}
                    >
                        {!isContentLoaded[popup.idx] ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                <ScrollArea
                                    className="max-h-[90vh] bg-white"
                                    style={{ width: popup.p_width_size, height: popup.p_height_size }}
                                >
                                    <div id={`popup_${popup.idx}`} className="ql-container ql-snow !border-none">
                                        <div
                                            className="ql-editor !p-0"
                                            dangerouslySetInnerHTML={{ __html: popup.p_content }}
                                        />
                                    </div>
                                </ScrollArea>
                                <div className="flex">
                                    {popup.p_one_day[0] === "Y" && (
                                        <button
                                            type="button"
                                            onClick={() => setOneDayClosedPopup(popup.idx)}
                                            className="flex-1 h-[60px] text-[18px] font-[500] text-white font-['GongGothicMedium'] bg-[#666] md:text-[24px] md:h-[70px]"
                                        >
                                            1일 동안 보지 않음
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setClosedPopup(popup.idx)}
                                        className="flex-1 h-[60px] text-[18px] font-[500] text-white font-['GongGothicMedium'] bg-[#222154] md:text-[24px] md:h-[70px]"
                                    >
                                        닫기
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            ))}
        </>
    );
}
